import React, { useEffect, useState } from 'react';
import { Bubble, Modal, Segment, Table, TableColumn } from 'tea-component';
import { autotip, scrollable } from 'tea-component/lib/table/addons';
import { useRequest } from 'ahooks';
import { useIntl } from 'umi';
import { getDataTablePreviewData, getTableInfoList } from '@dbl/services/dataSource';
import { getLocalText } from '@/pages/datalab/utils/utils';
import type { ITableColumnDto } from '@dbl/services/dataSource';
import { FieldType } from '@dbl/core/dataSource';
import {
  DimensionType,
  DimensionValue,
  Include255,
} from '@dbl/core/dataSource';
import './index.scss';

import { IPersonalDataDetailProps } from './types';

export default function PersonalDataDetail(props: IPersonalDataDetailProps) {
  const { showTableDetail, setShowTableDetail, tableDetailId, tableName, gameCode } = props;
  const { formatMessage, locale } = useIntl();
  
  // 检测当前语言
  const isEn = locale === 'en-US';
  
  const options = [
    { text: formatMessage({ id: 'dbl_personal_data_table_structure' }), value: '1' },
    { text: formatMessage({ id: 'dbl_personal_data_data_preview' }), value: '2' },
  ];
  const [curOptionValue, setCurOptionValue] = useState<string>('1');

  const [records, setRecords] = useState([]);
  const [columns, setColumns] = useState<any[]>([]);

  const [tableStructRecords, setTableStructRecords] = useState([]);

  const [tablePreviewDataRecords, setTablePreviewDataRecords] = useState([]);
  const [tablePreviewDataColumns, setTablePreviewDataColumns] = useState<any[]>([]);

  const {
    loading: runGetTableStructLoading,
    error: runGetTableStructError,
    runAsync: runGetTableStruct,
  } = useRequest(
    (tableId: string, gameCodeValue: string) =>
      getTableInfoList([tableId], gameCodeValue),
    {
    manual: true,
  });

  const {
    loading: runGetTablePreviewDataLoading,
    error: runGetTablePreviewDataError,
    runAsync: runGetTablePreviewData,
  } = useRequest(getDataTablePreviewData, {
    manual: true,
  });

  useEffect(() => {
    if (showTableDetail) {
      setCurOptionValue('1');
      setTableStructRecords([]);
      setTablePreviewDataRecords([]);
      setTablePreviewDataColumns([]);
      
      // 获取表结构信息
      runGetTableStruct(tableDetailId, gameCode).then((res: any) => {
        setTableStructRecords(res?.[0]?.column_list || []);
      });
      
      // 获取数据预览
      runGetTablePreviewData({
        table_id: tableDetailId,
      }).then((res: any) => {
        setTablePreviewDataColumns(buildPreviewDataColumns(res?.columns) || []);
        setTablePreviewDataRecords(res?.data_list || []);
      });
    }
  }, [showTableDetail, tableDetailId, gameCode]);

  useEffect(() => {
    switch (curOptionValue) {
      case '1':
        setRecords(tableStructRecords);
        setColumns(structColumns);
        break;
      case '2':
        setRecords(tablePreviewDataRecords);
        setColumns(tablePreviewDataColumns);
        break;
    }
  }, [curOptionValue, tableStructRecords, tablePreviewDataRecords]);

  const structColumns: TableColumn[] = [
    {
      key: 'display_name',
      header: formatMessage({ id: 'dbl_personal_data_column_name' }),
      width: 185,
      render(record: ITableColumnDto) {
        return (
          <div>
            {(isEn ? record.display_name_en : record.display_name) || record.display_name || record.name || '-'}
          </div>
        );
      },
    },
    {
      key: 'name',
      header: formatMessage({ id: 'dbl_personal_data_field_name' }),
      width: 185,
      render(record: ITableColumnDto) {
        return <div>{record.name || '-'}</div>;
      },
    },
    {
      key: 'type',
      header: formatMessage({ id: 'dbl_personal_data_field_type' }),
      width: 120,
      render(record: ITableColumnDto) {
        if (Object.values(FieldType).includes(record.type)) {
          // 映射 FieldType 到对应的国际化键
          const typeKeyMap: Record<string, string> = {
            [FieldType.Date]: 'dbl_multi_date',
            [FieldType.Time]: 'dbl_multi_date',
            [FieldType.String]: 'dbl_multi_string',
            [FieldType.Number]: 'dbl_multi_number',
            [FieldType.BigInt]: 'dbl_multi_number',
            [FieldType.Decimal]: 'dbl_multi_number',
            [FieldType.Float]: 'dbl_multi_float',
            [FieldType.Percent]: 'dbl_multi_percent',
          };
          const messageKey = typeKeyMap[record.type];
          return messageKey ? formatMessage({ id: messageKey }) : record.type;
        }
        return '-';
      },
    },
    {
      key: 'attribute',
      header: <div>{formatMessage({ id: 'dbl_personal_data_metric_dimension' })}</div>,
      width: 130,
      render(record: ITableColumnDto) {
        return record.is_metric ? formatMessage({ id: 'dbl_personal_data_metric' }) : formatMessage({ id: 'dbl_personal_data_dimension' });
      },
    },
    {
      key: 'dimension_setting',
      header: <div>{formatMessage({ id: 'dbl_personal_data_dimension_value_settings' })}</div>,
      width: 140,
      render(record: ITableColumnDto) {
        if (record.attribute === DimensionType.Metric) {
          return '-';
        }
        return record.dimension_setting === DimensionValue.Origin
          ? formatMessage({ id: 'dbl_personal_data_origin' })
          : formatMessage({ id: 'dbl_personal_data_system' });
      },
    },
    {
      key: 'flag',
      header: <div>{formatMessage({ id: 'dbl_personal_data_include_dimension_total_value' })}</div>,
      width: 160,
      render(record: ITableColumnDto) {
        if (record.attribute === DimensionType.Metric) {
          return '-';
        }
        return record.flag === Include255.Include
          ? formatMessage({ id: 'dbl_personal_data_include' })
          : formatMessage({ id: 'dbl_personal_data_exclude' });
      },
    },
    {
      key: 'remark',
      width: 240,
      header: formatMessage({ id: 'dbl_personal_data_field_desc' }),
      render(record: ITableColumnDto) {
        return <div>{getLocalText(isEn, record.remark, record.remark_en) || '-'}</div>;
      },
    },
  ];

  function buildPreviewDataColumns(resColumns: any[]): TableColumn[] {
    return resColumns.map((column: { name: string; is_sensitive: boolean; }) => {
      let newColumn: TableColumn = {
        key: column.name,
        header: column.name,
        width: 200,
      };
      if (column.is_sensitive) {
        newColumn = {
          ...newColumn,
          render: () => (
            <div>
              <Bubble dark content={formatMessage({ id: 'dbl_personal_data_not_show' })} placement={'top'}>
                ***
              </Bubble>
            </div>
          ),
        };
      }
      return newColumn;
    });
  }

  return (
    <Modal
      visible={showTableDetail}
      caption={<span style={{ fontSize: 16, fontWeight: 'bold' }}>{tableName}</span>}
      size="80%"
      onClose={() => {
        setShowTableDetail(false);
      }}
      className={'dbl-personal-data-detail dbl-add-source-detail-modal dbl-data-management-detail'}
    >
      <Modal.Body style={{height: 'calc(100% - 52px)'}}>
        <Segment
          className="dbl-personal-data-detail-segments"
          value={curOptionValue}
          onChange={(value) => setCurOptionValue(value)}
          options={options}
        />
        <Table
          columns={columns}
          records={records}
          addons={[
            scrollable({
              maxHeight: 'calc(100% - 74px)',
              minWidth: 1000,
              scrollToTopOnChange: true,
            }),
            autotip({
              isLoading: curOptionValue === '1' ? runGetTableStructLoading : runGetTablePreviewDataLoading,
              isError: curOptionValue === '1' ? !!runGetTableStructError : !!runGetTablePreviewDataError,
              style: { lineHeight: '200px' },
              emptyText: formatMessage({ id: 'dbl_common_not_search' }),
              loadingText: formatMessage({ id: 'dbl_common_loading' }),
              errorText: formatMessage({ id: 'dbl_common_load_fail' }),
              clearResultText: formatMessage({ id: 'dbl_common_go_back_old' }),
            }),
          ]}
        />
      </Modal.Body>
    </Modal>
  );
}
